
import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  Search, 
  Filter, 
  Briefcase, 
  MessageSquare, 
  Tag,
  CheckCircle,
  Star,
  Clock,
  Link as LinkIcon
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { projectCollaborations } from "@/data/project-collaborations";
import { CollaborationCard } from "@/components/collaborations/CollaborationCard";
import { CollaborationFilters } from "@/components/collaborations/CollaborationFilters";
import { CreateCollaborationForm } from "@/components/collaborations/CreateCollaborationForm";

const ProjectCollaborations = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("discover");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentCategory, setCurrentCategory] = useState("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<null | any>(null);

  // Filter projects based on search query and category
  const filteredProjects = projectCollaborations.filter(project => {
    const matchesSearch = searchQuery === "" || 
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = currentCategory === "all" || project.category === currentCategory;
    
    return matchesSearch && matchesCategory;
  });

  const openProjectDetails = (project: any) => {
    setSelectedProject(project);
    setIsDetailDialogOpen(true);
  };

  const handleApplyToProject = (projectId: string) => {
    toast({
      title: "Application Submitted",
      description: "Your application has been submitted successfully!",
      variant: "success"
    });
    setIsDetailDialogOpen(false);
  };

  const handleCreateCollaboration = (data: any) => {
    toast({
      title: "Project Created",
      description: "Your collaboration project has been posted successfully!",
      variant: "success"
    });
    setIsCreateDialogOpen(false);
    setActiveTab("my-projects");
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-4">
            Project Collaborations
          </h1>
          <p className="text-gray-600 max-w-3xl">
            Find teammates with complementary skills for your projects or join exciting collaborative opportunities. 
            Build your network and tackle larger, more complex projects together.
          </p>
        </motion.div>

        {/* Main Content with Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-8">
          <div className="flex justify-between items-center mb-6">
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="discover" className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                <span>Discover</span>
              </TabsTrigger>
              <TabsTrigger value="my-projects" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                <span>My Projects</span>
              </TabsTrigger>
              <TabsTrigger value="applied" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Applied</span>
              </TabsTrigger>
            </TabsList>
            
            <Button 
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white"
            >
              Create Collaboration
            </Button>
          </div>

          <TabsContent value="discover" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <CollaborationFilters 
                  currentCategory={currentCategory}
                  onCategoryChange={setCurrentCategory}
                />
              </div>

              <div className="lg:col-span-3 space-y-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                  <Input
                    placeholder="Search projects by title, skills, or description..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {filteredProjects.length > 0 ? (
                  <div className="grid gap-6 md:grid-cols-2">
                    {filteredProjects.map((project) => (
                      <CollaborationCard 
                        key={project.id}
                        project={project}
                        onViewDetails={() => openProjectDetails(project)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Briefcase className="mx-auto h-12 w-12 text-gray-300" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No projects found</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Try adjusting your search or filters to find more projects.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="my-projects" className="space-y-6">
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <Briefcase className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">No created projects yet</h3>
              <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                You haven't created any collaboration projects. Start building a team by posting your project needs.
              </p>
              <Button 
                onClick={() => setIsCreateDialogOpen(true)}
                className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Create Your First Collaboration
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="applied" className="space-y-6">
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <CheckCircle className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">No applications yet</h3>
              <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                You haven't applied to any collaboration projects. Explore available projects and start collaborating!
              </p>
              <Button 
                onClick={() => setActiveTab("discover")}
                className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Discover Projects
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Collaboration Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-500" />
                Create Collaboration Project
              </DialogTitle>
            </DialogHeader>
            <CreateCollaborationForm onSubmit={handleCreateCollaboration} onCancel={() => setIsCreateDialogOpen(false)} />
          </DialogContent>
        </Dialog>

        {/* Project Details Dialog */}
        {selectedProject && (
          <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  {selectedProject.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={selectedProject.owner.avatar} />
                      <AvatarFallback>{selectedProject.owner.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{selectedProject.owner.name}</div>
                      <div className="text-sm text-gray-500">Project Owner</div>
                    </div>
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200">
                    {selectedProject.category}
                  </Badge>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="text-lg font-medium mb-2">Project Description</h3>
                  <p className="text-gray-700">{selectedProject.description}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-2">Skills Needed</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.skillsNeeded.map((skill: string, index: number) => (
                      <Badge key={index} variant="outline" className="bg-gray-100">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Timeline</h3>
                    <p className="text-gray-900">{selectedProject.timeline}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Budget Range</h3>
                    <p className="text-gray-900">{selectedProject.budget}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Team Size</h3>
                    <p className="text-gray-900">{selectedProject.teamSize} members</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Location</h3>
                    <p className="text-gray-900">{selectedProject.location}</p>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="text-lg font-medium mb-4">Apply to Collaborate</h3>
                  <Textarea 
                    placeholder="Explain why you'd be a good fit for this project and what skills you bring to the team..."
                    className="min-h-[120px]"
                  />
                </div>
              </div>
              
              <DialogFooter className="flex space-x-2">
                <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => handleApplyToProject(selectedProject.id)}>
                  Submit Application
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Why Collaborate Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold mb-6">Why Collaborate?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Expand Your Capabilities",
                description: "Take on larger, more complex projects by collaborating with freelancers who have complementary skills.",
                icon: <Users className="h-10 w-10 text-indigo-500" />
              },
              {
                title: "Share Responsibilities",
                description: "Distribute workload and responsibilities among team members to deliver projects more efficiently.",
                icon: <Briefcase className="h-10 w-10 text-indigo-500" />
              },
              {
                title: "Grow Your Network",
                description: "Build your professional network and create lasting relationships for future opportunities.",
                icon: <LinkIcon className="h-10 w-10 text-indigo-500" />
              }
            ].map((card, index) => (
              <Card key={index} className="border-t-4 border-t-indigo-500">
                <CardHeader>
                  <div className="mb-2">{card.icon}</div>
                  <CardTitle>{card.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{card.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectCollaborations;
